"use client";

import { useQuery } from "@tanstack/react-query";
import { Building2, Check, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

import { Popover } from "@/components/ui-system";
import { listTenants } from "@/lib/api/endpoints/tenants";
import { cn } from "@/lib/utils/cn";
import { isSuperAdmin } from "@/modules/auth/permissions";
import { useAuthStore } from "@/modules/auth/store";

function shortenId(id: string | null | undefined): string {
  if (!id) return "No workspace";
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

export type TenantSwitcherProps = { tenantId: string | null; className?: string };

export function TenantSwitcher({ tenantId, className }: TenantSwitcherProps) {
  const user = useAuthStore((state) => state.user);
  const activeTenantId = useAuthStore((state) => state.tenantId);
  const setTenantId = useAuthStore((state) => state.setTenantId);
  const tenantsQuery = useQuery({
    queryKey: ["tenant-memberships", user?.id ?? ""],
    queryFn: () => listTenants(),
    enabled: Boolean(user?.id),
    staleTime: 60_000,
  });
  const memberships = tenantsQuery.data ?? user?.tenants ?? [];
  const selected = memberships.find((workspace) => workspace.id === activeTenantId) ?? memberships[0] ?? null;

  return (
    <Popover
      className={className}
      placement="bottom-end"
      ariaLabel="Select workspace"
      contentClassName="w-72 p-0"
      trigger={
        <span id="workspace-tenant-trigger" className="ui-control inline-flex h-10 min-h-10 max-w-[16rem] min-w-[12rem] items-center gap-ds-2 px-ds-2 py-ds-1.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-accent-muted text-accent"><Building2 className="h-4 w-4" aria-hidden /></span>
          <span className="min-w-0 flex-1 text-left"><span className="block text-micro uppercase tracking-[0.08em] text-muted">Workspace</span><span className="block truncate text-caption font-semibold">{selected?.name ?? shortenId(tenantId)}</span></span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted" aria-hidden />
        </span>
      }
    >
      {(close) => (
        <>
          <div className="border-b border-card-border px-ds-4 py-ds-3"><p className="text-caption font-semibold">Active workspace</p><p className="mt-ds-1 text-micro text-muted">Scopes devices, dashboards, organizations, and platform data.</p></div>
          <div className="max-h-72 overflow-y-auto p-ds-2">
            {memberships.length ? memberships.map((workspace) => {
              const active = workspace.id === activeTenantId;
              return (
                <button
                  key={workspace.id}
                  type="button"
                  className={cn("flex w-full items-center gap-ds-2 rounded-control px-ds-3 py-ds-2 text-left transition-colors duration-fast", active ? "bg-accent-muted text-foreground" : "text-muted hover:bg-surface-hover hover:text-foreground")}
                  onClick={() => {
                    close();
                    if (workspace.id !== activeTenantId) {
                      setTenantId(workspace.id);
                      window.location.reload();
                    }
                  }}
                >
                  <span className="min-w-0 flex-1"><span className="block truncate text-caption font-semibold">{workspace.name}</span><span className="block truncate text-micro text-muted">{workspace.domain ?? shortenId(workspace.id)}</span></span>
                  {active ? <Check className="h-4 w-4 text-accent" aria-hidden /> : null}
                </button>
              );
            }) : <p className="px-ds-3 py-ds-5 text-center text-caption text-muted">{tenantsQuery.isLoading ? "Loading workspaces…" : "No workspaces assigned."}</p>}
          </div>
          {isSuperAdmin(user) ? <div className="border-t border-card-border p-ds-2"><Link to="/tenants" onClick={close} className="flex min-h-9 items-center justify-center rounded-control px-ds-3 text-caption font-semibold text-accent hover:bg-accent-muted">Manage workspaces</Link></div> : null}
        </>
      )}
    </Popover>
  );
}
